import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { PlusJakartaSansFontBase64String } from "../../Helpers/PlusJakartaSans-base64String";
import { imgData } from "../../Helpers/smestLogoBase64ImgData";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: { finalY: number };
    getImageProperties?: (imageData: string | HTMLImageElement) => {
      width: number;
      height: number;
    };
  }
}

export interface BondInfo {
  isin?: string;
  taxCategory?: string;
  securityType?: { type?: string };
}

export interface CalculatedValues {
  count?: number | string;
}

export interface ICashflow {
  date: string; // DD/MM/YYYY
  amount: number | string;
  type: "Interest" | "Maturity";
}

// ---------- Utilities ----------
const toNumber = (x?: string | number | null) => {
  if (x == null) return 0;
  if (typeof x === "number") return isFinite(x) ? x : 0;
  const cleaned = x.toString().replace(/[^\d.-]/g, "");
  const n = Number(cleaned);
  return isFinite(n) ? n : 0;
};

export const toINR = (value: any, fraction: number) => {
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: fraction,
  });
  return formatter.format(value);
};

export function is_gsec_sdl_tBill(securityType: string) {
  return (
    securityType === "State Development Loans" ||
    securityType === "Government Securities" ||
    securityType === "T Bill" ||
    securityType === "Treasury Bill"
  );
}

// ---------- Renderer ----------
export async function generateBondQuotationSecondPage(
  doc: jsPDF,
  bond: BondInfo,
  calculated: CalculatedValues,
  cashflow: ICashflow[]
): Promise<{ doc: jsPDF; nextY: number }> {
  // Measurements
  const M = 36;
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Font (optional, same as page 1)
  if (PlusJakartaSansFontBase64String) {
    doc.addFileToVFS(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      PlusJakartaSansFontBase64String
    );
    doc.addFont(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      "PlusJakartaSans",
      "normal"
    );
    doc.addFont(
      "PlusJakartaSans-VariableFont_wght-normal.ttf",
      "PlusJakartaSans",
      "bold"
    );
    doc.setFont("PlusJakartaSans", "normal");
  }

  // Prepare rows
  const noOfBondsRaw = calculated?.count;
  const noOfBonds = Number(noOfBondsRaw);
  const showTDS = !(
    is_gsec_sdl_tBill(bond?.securityType?.type || "") ||
    bond?.taxCategory === "Tax Free"
  );

  // Sort cashflow by date ascending (DD/MM/YYYY)
  const rows = (cashflow || []).slice().sort((a, b) => {
    const ma = moment(a.date, "DD/MM/YYYY").valueOf();
    const mb = moment(b.date, "DD/MM/YYYY").valueOf();
    return ma - mb;
  });

  const body = rows.map((cf) => {
    const m = moment(cf.date, "DD/MM/YYYY");
    const year = m.isValid() ? m.format("YYYY") : "-";
    const payDate = m.isValid() ? m.format("DD MMM") : "-";
    const baseAmt = toNumber(cf.amount);
    const amt = baseAmt * (isNaN(noOfBonds) ? 1 : noOfBonds);
    const amtStr = toINR(amt, 2);
    const tdsAmt = cf.type === "Interest" ? amt * 0.9 : amt;
    const tdsStr = toINR(tdsAmt, 2);

    if (showTDS) {
      return [year, payDate, amtStr, tdsStr, cf.type];
    }
    return [year, payDate, amtStr, cf.type];
  });

  // Build table headers based on showTDS
  const head = showTDS
    ? [["Year", "Payment Date", "Amount", "Amount after TDS", "Payout Type"]]
    : [["Year", "Payment Date", "Amount", "Payout Type"]];

  // Header drawing (logo + title) on EVERY page of this table
  const headerHeight = 42;
  const drawHeader = () => {
    // Company logo
    try {
      if (imgData) {
        const dataUrl = imgData.startsWith("data:")
          ? imgData
          : `data:image/png;base64,${imgData}`;
        const maxW = 140,
          maxH = 48;
        let drawW = 120,
          drawH = 40;
        if (typeof (doc as any).getImageProperties === "function") {
          const p = (doc as any).getImageProperties!(dataUrl);
          const scale = Math.min(
            maxW / (p.width || maxW),
            maxH / (p.height || maxH)
          );
          drawW = (p.width || maxW) * scale;
          drawH = (p.height || maxH) * scale;
        }
        doc.addImage(dataUrl, "PNG", W - M - drawW, M - 6, drawW, drawH);
      }
    } catch {}
    // Title
    doc.setFontSize(18);
    doc.setFont("PlusJakartaSans", "bold");
    doc.setTextColor(20);
    doc.text(`Cashflow for ${bond?.isin ?? "-"}`, M, M + 24);
  };

  // Compute table width and exact column widths
  const tableW = W - 2 * M;

  let colStyles: any;
  if (showTDS) {
    const f0 = 0.12,
      f1 = 0.19,
      f2 = 0.23,
      f3 = 0.28; // remainder -> last col
    const w0 = Math.round(tableW * f0);
    const w1 = Math.round(tableW * f1);
    const w2 = Math.round(tableW * f2);
    const w3 = Math.round(tableW * f3);
    const w4 = tableW - (w0 + w1 + w2 + w3);
    colStyles = {
      0: { cellWidth: w0, halign: "left" },
      1: { cellWidth: w1, halign: "left" },
      2: { cellWidth: w2, halign: "left" }, // Amount LEFT
      3: { cellWidth: w3, halign: "left" }, // Amount after TDS LEFT
      4: { cellWidth: w4, halign: "left" },
    };
  } else {
    const f0 = 0.14,
      f1 = 0.2,
      f2 = 0.38; // remainder -> last
    const w0 = Math.round(tableW * f0);
    const w1 = Math.round(tableW * f1);
    const w2 = Math.round(tableW * f2);
    const w3 = tableW - (w0 + w1 + w2);
    colStyles = {
      0: { cellWidth: w0, halign: "left" },
      1: { cellWidth: w1, halign: "left" },
      2: { cellWidth: w2, halign: "left" }, // Amount LEFT
      3: { cellWidth: w3, halign: "left" },
    };
  }

  (doc as any).autoTable({
    head,
    body,
    theme: "grid",
    styles: {
      font: PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      fontSize: 10,
      cellPadding: 6,
      lineWidth: 0.1,
      lineColor: 210,
      textColor: 30,
      halign: "left",
      valign: "middle",
    },
    headStyles: {
      fillColor: [248, 248, 248],
      textColor: 20,
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: colStyles,
    margin: { top: M + headerHeight, left: M, right: M },
    tableWidth: tableW,
    didDrawPage: () => {
      drawHeader();
    },
    didParseCell: (data: any) => {
      // Emphasize "Maturity" in payout column (green + bold)
      if (data.section === "body" && !showTDS) {
        if (data.column.index === 3 && data.cell.raw === "Maturity") {
          data.cell.styles.textColor = [34, 120, 66];
          data.cell.styles.fontStyle = "bold";
        }
      }
      if (data.section === "body" && showTDS) {
        if (data.column.index === 4 && data.cell.raw === "Maturity") {
          data.cell.styles.textColor = [34, 120, 66];
          data.cell.styles.fontStyle = "bold";
        }
      }
    },
  });

  let endY = (doc as any).lastAutoTable?.finalY || M + headerHeight + 20;

  // Disclaimer (place after the table; if it doesn't fit, push to next page and redraw header)
  const disclaimer =
    "Disclaimer: The actual dates and interest amounts can vary depending on Bank closures, holidays etc. " +
    "Tax Deducted at Source (TDS) is applicable on interest receivable based on relevant tax regulations. " +
    "You can avail TDS credit while filing your Income Tax Return (ITR).";

  const discBlock = (yStart: number) => {
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.setFont(
      PlusJakartaSansFontBase64String ? "PlusJakartaSans" : "helvetica",
      "normal"
    );
    const maxW = W - 2 * M;
    const split = doc.splitTextToSize(disclaimer, maxW);
    doc.text(split, M, yStart);
    // Rough height estimate
    const lineHeight = 8 * 1.25;
    return yStart + split.length * lineHeight;
  };

  let nextY: number;
  const minDiscH = 40;
  if (endY + 18 + minDiscH > H - M) {
    doc.addPage("a4");
    drawHeader();
    const yAfterDisc = discBlock(M + headerHeight + 12);
    nextY = yAfterDisc + 14; // padding after disclaimer
  } else {
    const yAfterDisc = discBlock(endY + 18);
    nextY = yAfterDisc + 14;
  }

  // Expose cursor for chaining
  (doc as any).__nextY = nextY;
  (doc as any).__headerHeight = headerHeight;
  (doc as any).__margin = M;

  return { doc, nextY };
}
